import { Flex } from 'component/base/Grid';
import { rotate } from 'component/ui/Animations/Rotate';
import type * as CSS from 'csstype';
import { Design, Intent } from 'enums';
import { ThemeColors } from 'providers/Theme';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

/**
 * Default button intent styles
 */
export const intent = css``;

/**
 * PRIMARY button intent
 */
export const intentPrimary = css`
  ${intent}

  color: ${(props) => props.theme.intent.primary.foreground};
  background-color: ${(props) => props.theme.intent.primary.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.primary.hover};
  }

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: ${(props) => props.theme.intent.primary.foreground};
  }
`;

/**
 * SECONDARY button intent
 */
export const intentSecondary = css`
  ${intent}

  color: ${(props) => props.theme.intent.secondary.foreground};
  background-color: ${(props) => props.theme.intent.secondary.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.secondary.hover};
  }
  box-shadow: inset 0 0px 0px 1px
    ${(props) => props.theme.colors.lightPeriwinkle};
  padding: 0.5rem 1rem;
  outline: none;

  transition: fill 0.3s ease-in-out;
  fill: ${(props) => props.theme.intent.secondary.foreground};
  &:hover {
    fill: ${(props) => props.theme.colors.darkBlue};
  }
`;

/**
 * SECONDARY button intent
 */
export const intentTertiary = css`
  background-color: transparent;
  border: 1px solid ${(props) => props.theme.colors.lightPeriwinkle};
  transition: border-color 120ms linear;

  &:hover {
    background-color: transparent;
    border-color: ${(props) => props.theme.colors.blue};
  }
`;

/**
 * PRIMARY button intent
 */
export const intentQuaternary = css`
  ${intent}

  color: ${(props) => props.theme.intent.quaternary.foreground};
  background-color: ${(props) => props.theme.intent.quaternary.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.quaternary.hover};
  }

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: ${(props) => props.theme.intent.quaternary.foreground};
  }
`;

export const intentQuinary = css`
  ${intent}

  color: ${(props) => props.theme.intent.quinary.foreground};
  background-color: ${(props) => props.theme.intent.quinary.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.quinary.hover};
  }

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: ${(props) => props.theme.intent.quinary.foreground};
  }
`;

/**
 * SUCCESS button intent
 */
export const intentSuccess = css`
  ${intent}

  color: ${(props) => props.theme.intent.success.foreground};
  background-color: ${(props) => props.theme.intent.success.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.success.hover};
  }
`;

/**
 * WARNING button intent
 */
export const intentWarning = css`
  ${intent}

  color: ${(props) => props.theme.intent.warning.foreground};
  background-color: ${(props) => props.theme.intent.warning.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.warning.hover};
  }
`;

/**
 * DANGER button intent
 */
export const intentDanger = css`
  ${intent}

  color: ${(props) => props.theme.intent.danger.foreground};
  background-color: ${(props) => props.theme.intent.danger.background};
  &:hover {
    background-color: ${(props) => props.theme.intent.danger.hover};
  }
`;

interface IDefaultButton {
  $intent?: Intent;
  $design?: Design;
  $submitting?: boolean;
  $display?: string;
  $invalid?: boolean;
  $block?: boolean;
  $active?: boolean;
}

/**
 * Standard button styles
 */
export const DefaultButton = styled.button<
  IDefaultButton & React.ButtonHTMLAttributes<HTMLButtonElement>
>`
  height: 36px;
  font-size: 14px;
  border-radius: 3px;
  display: ${(props) =>
    props.$display || (props.$block ? 'block' : 'inline-block')};

  font-weight: 600;
  background-color: ${(props) => props.theme.colors.yellow};
  color: ${(props) => props.theme.colors.darkBlue};
  padding: 0.5rem 1rem;
  position: relative;
  outline: none;
  transition: background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

  white-space: nowrap;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.$block &&
    css`
      width: 100%;
    `}

  &:disabled {
    opacity: 0.7;
    pointer-events: none;
    transition: background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

    > * {
      cursor: not-allowed;
      pointer-events: initial;
    }
  }

  ${(props) =>
    props.$display === 'block' &&
    css`
      width: 100%;
    `}

  ${(props) =>
    props.$design === Design.LARGE &&
    css`
      height: 50px;
      padding: 1em 2em;
      font-size: 1.15em;
      border-radius: 4px;
    `}

  ${(props) =>
    props.$design === Design.SMALL &&
    css`
      height: 32px;
      padding: 0 0.75rem;
      font-size: 14px;
    `}

  // when not submitting
  ${(props) =>
    !props.$submitting &&
    css`
      cursor: pointer;
      background-color: ${props.theme.colors.yellow};

      &:hover {
        background-color: ${props.theme.colors.yellow};
      }
    `}

  // when submitting
  ${(props) =>
    props.$submitting &&
    css`
      > span {
        height: 15px;
        display: flex;
        align-content: center;
        align-items: center;
        justify-content: center;
      }
    `}

  // when not valid
  ${(props) =>
    props.$invalid &&
    css`
      opacity: 0.7;

      &:hover {
        background-color: ${props.theme.colors.yellow};
        cursor: not-allowed;
      }
    `}
  
    ${({ $active, theme, $design }) =>
    $design === Design.TEXT &&
    css`
      background-color: transparent;
      color: ${$active ? theme.colors.blue : theme.colors.darkBlue};
      font-size: 14px;
      font-weight: 500;
      line-height: normal;
      display: inline-flex;
      height: unset;
      padding: unset;
      margin: 0;

      &:hover {
        background-color: transparent;
        color: ${theme.colors.blue};
        & * {
          color: ${theme.colors.blue};
        }
      }
    `}

  ${(props) =>
    props.$intent === Intent.PRIMARY &&
    css`
      ${intentPrimary}
    `}

  ${(props) =>
    props.$intent === Intent.SECONDARY &&
    css`
      ${intentSecondary}
    `}

  ${(props) =>
    props.$intent === Intent.TERTIARY &&
    css`
      ${intentTertiary}
    `}

  ${(props) =>
    props.$intent === Intent.QUATERNARY &&
    css`
      ${intentQuaternary}
    `}

  ${(props) =>
    props.$intent === Intent.QUINARY &&
    css`
      ${intentQuinary}
    `}

  ${(props) =>
    props.$intent === Intent.SUCCESS &&
    css`
      ${intentSuccess}
    `}

  ${(props) =>
    props.$intent === Intent.WARNING &&
    css`
      ${intentWarning}
    `}

  ${(props) =>
    props.$intent === Intent.DANGER &&
    css`
      ${intentDanger}
    `}
`;

interface IDefaultButtonLink {
  $intent?: Intent;
  $design?: Design;
}

/**
 * Button Link styles
 */
export const DefaultButtonLink = styled(DefaultButton).attrs({
  as: Link,
})<IDefaultButtonLink>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.$design === Design.STANDARD &&
    css`
      line-height: 20px;
    `}

  ${(props) =>
    props.$intent === Intent.PRIMARY &&
    css`
      ${intentPrimary}
    `}

  ${(props) =>
    props.$intent === Intent.SECONDARY &&
    css`
      ${intentSecondary}
    `}

  ${(props) =>
    props.$intent === Intent.TERTIARY &&
    css`
      ${intentTertiary}
    `}

  ${(props) =>
    props.$intent === Intent.SUCCESS &&
    css`
      ${intentSuccess}
    `}

  ${(props) =>
    props.$intent === Intent.WARNING &&
    css`
      ${intentWarning}
    `}

  ${(props) =>
    props.$intent === Intent.DANGER &&
    css`
      ${intentDanger}
    `}
`;

export const AriaHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

export const DefaultButtonAction = styled.a`
  display: block;
  margin: 20px;
`;

interface IDefaultButtonIcon {
  $minimal?: boolean;
  $intent?: Intent;
  $design?: Design;
  $rotate?: boolean;
  $height?: string;
  $width?: string;
  $disabled?: boolean;
  $borderRadius?: string;
  $border?: string;
  $margin?: CSS.Property.Margin;
  $minWidth?: CSS.Property.MinWidth;
  $padding?: CSS.Property.Padding;
}

/**
 * Button Icon styles
 */
export const DefaultButtonIcon = styled.button<IDefaultButtonIcon>`
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
  border-radius: ${({ $borderRadius }) => $borderRadius || '4px'};
  border: ${({ $border }) => $border};
  cursor: pointer;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ $minWidth, $width }) => $minWidth || $width};
  ${({ $margin }) =>
    css`
      margin: ${$margin};
    `}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.lightBlueGrey}; // Just an example, adjust as needed
  }

  :disabled {
    pointer-events: none;

    > * {
      cursor: not-allowed;
      pointer-events: initial;
    }
  }

  ${(props) =>
    props.$design === Design.LARGE &&
    css`
      height: 50px;
      padding: 1em 2em;
      font-size: 1.15em;
      border-radius: 4px;
    `}

  ${(props) =>
    props.$design === Design.SMALL &&
    css`
      height: 32px;
      padding: 0 0.75rem;
      font-size: 14px;
    `}

  ${(props) =>
    props.$minimal &&
    css`
      border-radius: unset;
      width: auto;
      height: auto;
    `}

  ${(props) =>
    props.$intent === Intent.PRIMARY &&
    css`
      ${intentPrimary}
    `}

  ${(props) =>
    props.$intent === Intent.SECONDARY &&
    css`
      ${intentSecondary}
    `}

  ${(props) =>
    props.$intent === Intent.TERTIARY &&
    css`
      ${intentTertiary}
    `}
  
  ${(props) =>
    props.$intent === Intent.QUATERNARY &&
    css`
      ${intentQuaternary}
    `}

  ${(props) =>
    props.$intent === Intent.QUINARY &&
    css`
      ${intentQuinary}
    `}


  ${(props) =>
    props.$intent === Intent.SUCCESS &&
    css`
      ${intentSuccess}
    `}

  ${(props) =>
    props.$intent === Intent.WARNING &&
    css`
      ${intentWarning}
    `}

  ${(props) =>
    props.$intent === Intent.DANGER &&
    css`
      ${intentDanger}
    `}

  ${(props) => props.$design === Design.STANDARD && css``}

  ${({ $rotate }) => $rotate && rotate}
  
  ${(props) =>
    props.$design === Design.LARGE &&
    css`
      svg {
        max-width: 24px;
        max-height: 24px;
      }
    `}


  ${(props) =>
    props.$design === Design.SMALL &&
    css`
      transition: background-color 0.2s ease-in;

      &:focus {
        outline: none;
        background-color: #e5e5e5;
      }

      :hover {
        background-color: #f2f2f2;
      }
    `}
    
  ${({ $padding }) =>
    $padding
      ? css`
          padding: ${$padding};
        `
      : ''};
`;

DefaultButtonIcon.defaultProps = {
  $height: '36px',
  $width: '36px',
  $rotate: false,
};

interface IRawButton {
  $textColor?: ThemeColors;
}

export const RawButton = styled.button<IRawButton>`
  cursor: pointer;

  ${(props) =>
    props.$textColor &&
    `
    color: ${props.theme.colors[props.$textColor]};
  `};
`;

export const LoadingContainer = styled(Flex)`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
`;

interface IContent {
  $submitting: boolean;
}

export const Content = styled(Flex)<IContent>`
  white-space: nowrap;
  align-items: center;
  justify-content: center;

  // when submitting set text to hidden to maintain button size

  ${(props) =>
    props.$submitting &&
    css`
      visibility: hidden;
    `}
`;
